import * as THREE from 'three/webgpu';
import type { ResolvedGeometryEntity } from '../reference-point/ReferencePointSolver';
import { ReferenceCoordinateFrame } from '../ReferenceFeatureTypes';
import {
  tryGetLineEndpoints,
  isPointOnBothSegments,
  computePlanarSegmentIntersection,
  projectPointToPlane,
  normalizeVec3,
  clampNumber,
  buildAnyPerpendicularUnitVec3,
  projectPointOnSupportedSurface,
} from './PointFeatureSolveContextGeometryUtils';

/**
 * 克隆三维向量。
 *
 * @param value 输入向量。
 * @returns 克隆后的向量；缺失时返回 null。
 */
function cloneVector3(value: THREE.Vector3 | null | undefined): THREE.Vector3 | null {
  // 这里不再解析未知结构；上游几何源必须已经产出 THREE.Vector3。
  return value?.clone() ?? null;
}

/**
 * 克隆三维向量数组。
 *
 * @param value 输入向量数组。
 * @returns 克隆后的向量数组。
 */
function cloneVector3Array(value: readonly THREE.Vector3[] | null | undefined): THREE.Vector3[] {
  // 交点和投影候选必须由源计算函数直接提供 Vector3[]，运行时只负责隔离可变引用。
  return value?.map((point) => point.clone()) ?? [];
}

/**
 * 计算面的中心点。
 *
 * @param face 面实体。
 * @returns 面中心点；不可用时返回 null。
 */
export function getFaceCentroidByEntity(face: ResolvedGeometryEntity): THREE.Vector3 | null {
  return cloneVector3(face.data.centroid);
}

/**
 * 计算两个实体的交点集合。
 *
 * @param entityA 实体 A。
 * @param entityB 实体 B。
 * @returns 交点集合。
 */
export function intersectEntitiesByEntity(
  entityA: ResolvedGeometryEntity,
  entityB: ResolvedGeometryEntity,
): THREE.Vector3[] {
  const key = `${entityA.id}:${entityB.id}`;
  const direct = entityA.data.intersections;
  const reverse = entityB.data.intersections;
  const explicitCandidates = cloneVector3Array(direct?.[entityB.id] ?? direct?.[key] ?? reverse?.[entityA.id]);
  const lineA = tryGetLineEndpoints(entityA);
  const lineB = tryGetLineEndpoints(entityB);
  if (explicitCandidates.length > 0) {
    if (lineA && lineB) {
      const validated = explicitCandidates.filter((point) => isPointOnBothSegments(
        point,
        lineA.start,
        lineA.end,
        lineB.start,
        lineB.end,
      ));
      if (validated.length > 0) {
        return validated;
      }
    } else if (explicitCandidates.length > 0) {
      return explicitCandidates;
    }
  }

  const aCenter = cloneVector3(entityA.data.center);
  const aRadius = Number(entityA.data.radius ?? Number.NaN);
  const bCenter = cloneVector3(entityB.data.center);
  const bRadius = Number(entityB.data.radius ?? Number.NaN);
  if (aCenter && bCenter && Number.isFinite(aRadius) && Number.isFinite(bRadius)) {
    const results: THREE.Vector3[] = [];
    const dx = bCenter.x - aCenter.x;
    const dy = bCenter.y - aCenter.y;
    const distance = Math.hypot(dx, dy);
    const epsilon = 1e-9;
    if (distance > epsilon && distance <= aRadius + bRadius + epsilon && distance + epsilon >= Math.abs(aRadius - bRadius)) {
      const a = (aRadius * aRadius - bRadius * bRadius + distance * distance) / (2 * distance);
      const h2 = aRadius * aRadius - a * a;
      if (h2 >= -epsilon) {
        const h = Math.sqrt(Math.max(0, h2));
        const xm = aCenter.x + (a * dx) / distance;
        const ym = aCenter.y + (a * dy) / distance;
        const rx = -dy * (h / distance);
        const ry = dx * (h / distance);
        const t = a / distance;
        const mz = aCenter.z + t * (bCenter.z - aCenter.z);
        results.push(new THREE.Vector3(xm + rx, ym + ry, mz));
        if (h > epsilon) {
          results.push(new THREE.Vector3(xm - rx, ym - ry, mz));
        }
      }
    }
    if (results.length > 0) {
      return results;
    }
  }

  if (!lineA || !lineB) {
    return [];
  }
  const fallbackPoint = computePlanarSegmentIntersection(lineA.start, lineA.end, lineB.start, lineB.end);
  return fallbackPoint ? [fallbackPoint] : [];
}

/**
 * 沿目标法向进行点投影。
 *
 * @param point 源点。
 * @param target 目标实体。
 * @returns 投影点集合。
 */
export function projectPointNormalByEntity(
  point: THREE.Vector3,
  target: ResolvedGeometryEntity,
): THREE.Vector3[] {
  const surfaceProjectionPoint = projectPointOnSupportedSurface(target.data.surfaceObject, point);
  if (surfaceProjectionPoint) {
    return [surfaceProjectionPoint];
  }

  const planeNormal = cloneVector3(target.data.planeNormal ?? target.data.normal);
  const planePoint = cloneVector3(target.data.planePoint ?? target.data.point ?? target.data.centroid);
  if (planeNormal && planePoint) {
    const projected = projectPointToPlane(point, planePoint, planeNormal);
    if (projected) {
      return [projected];
    }
  }

  return cloneVector3Array(target.data.projection);
}

/**
 * 读取点性实体坐标。
 *
 * @param entity 目标实体。
 * @returns 点坐标；不可用时返回 null。
 */
export function getEntityPointByEntity(entity: ResolvedGeometryEntity): THREE.Vector3 | null {
  return cloneVector3(entity.data.point);
}

/**
 * 获取曲线长度。
 *
 * @param curve 目标曲线。
 * @returns 曲线长度；不可用时返回 null。
 */
export function getCurveLengthByEntity(curve: ResolvedGeometryEntity): number | null {
  const value = curve.data.length;
  if (Number.isFinite(value)) {
    return Number(value);
  }

  const radius = Number(curve.data.radius ?? Number.NaN);
  const radiusX = Number(curve.data.radiusX ?? Number.NaN);
  const radiusY = Number(curve.data.radiusY ?? Number.NaN);
  const angleRange = Number(curve.data.angleRange ?? Number.NaN);
  if (Number.isFinite(radius) && radius > 0) {
    if (Number.isFinite(angleRange) && Math.abs(angleRange) > 1e-12) {
      return Math.abs(radius * angleRange);
    }
    return 2 * Math.PI * radius;
  }
  if (Number.isFinite(radiusX) && Number.isFinite(radiusY) && radiusX > 0 && radiusY > 0) {
    const a = radiusX;
    const b = radiusY;
    const h = ((a - b) * (a - b)) / ((a + b) * (a + b));
    const circumference = Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
    if (Number.isFinite(angleRange) && Math.abs(angleRange) > 1e-12) {
      return Math.abs(circumference * (angleRange / (2 * Math.PI)));
    }
    return circumference;
  }

  const start = cloneVector3(curve.data.startPoint);
  const end = cloneVector3(curve.data.endPoint);
  if (start && end) {
    const length = end.clone().sub(start).length();
    return Number.isFinite(length) ? length : null;
  }
  return null;
}

/**
 * 按弧长在曲线上取点。
 *
 * @param curve 目标曲线。
 * @param length 弧长。
 * @returns 取样点；不可用时返回 null。
 */
export function evaluateCurveAtLengthByEntity(curve: ResolvedGeometryEntity, length: number): THREE.Vector3 | null {
  const center = cloneVector3(curve.data.center);
  const radius = Number(curve.data.radius ?? Number.NaN);
  const angleRange = Number(curve.data.angleRange ?? Number.NaN);
  const clockwise = typeof curve.data.clockwise === 'boolean' ? Boolean(curve.data.clockwise) : null;
  const startPoint = cloneVector3(curve.data.startPoint);
  const endPoint = cloneVector3(curve.data.endPoint);
  const xAxisRaw = cloneVector3(curve.data.xAxis);
  const yAxisRaw = cloneVector3(curve.data.yAxis);
  const normalRaw = cloneVector3(curve.data.normal);
  const xAxis = xAxisRaw ? normalizeVec3(xAxisRaw) : null;
  const yAxis = yAxisRaw ? normalizeVec3(yAxisRaw) : null;
  const explicitNormal = normalRaw ? normalizeVec3(normalRaw) : null;
  if (center && Number.isFinite(radius) && radius > 0) {
    const startRadial = startPoint ? normalizeVec3(startPoint.clone().sub(center)) : null;
    const endRadial = endPoint ? normalizeVec3(endPoint.clone().sub(center)) : null;

    let uUnit: THREE.Vector3 | null = startRadial ?? xAxis ?? yAxis ?? new THREE.Vector3(1, 0, 0);
    uUnit = normalizeVec3(uUnit ?? new THREE.Vector3(1, 0, 0));
    if (!uUnit) {
      return null;
    }

    let normal: THREE.Vector3 | null = explicitNormal;
    if (!normal && xAxis && yAxis) {
      normal = normalizeVec3(xAxis.clone().cross(yAxis));
    }
    if (!normal && startRadial && endRadial) {
      normal = normalizeVec3(startRadial.clone().cross(endRadial));
    }

    let vUnit: THREE.Vector3 | null = null;
    if (normal) {
      vUnit = normalizeVec3(normal.clone().cross(uUnit));
    }
    if (!vUnit && yAxis) {
      const projected = yAxis.dot(uUnit);
      vUnit = normalizeVec3(yAxis.clone().addScaledVector(uUnit, -projected));
    }
    if (!vUnit) {
      vUnit = buildAnyPerpendicularUnitVec3(uUnit);
    }
    if (!vUnit) {
      return null;
    }
    if (!normal) {
      normal = normalizeVec3(uUnit.clone().cross(vUnit));
    } else {
      const alignedV = normalizeVec3(normal.clone().cross(uUnit));
      if (alignedV) {
        const direction = alignedV.dot(vUnit) < 0 ? -1 : 1;
        vUnit = alignedV.multiplyScalar(direction);
      }
    }

    let totalAngle = 2 * Math.PI;
    if (Number.isFinite(angleRange) && Math.abs(angleRange) > 1e-12) {
      if (clockwise === true) {
        totalAngle = -Math.abs(angleRange);
      } else if (clockwise === false) {
        totalAngle = Math.abs(angleRange);
      } else {
        totalAngle = angleRange;
      }
    } else if (startRadial && endRadial) {
      const cross = startRadial.clone().cross(endRadial);
      const crossLength = cross.length();
      const dot = clampNumber(startRadial.dot(endRadial), -1, 1);
      const unsignedAngle = Math.atan2(crossLength, dot);
      if (unsignedAngle > 1e-12) {
        if (normal) {
          const direction = Math.sign(normal.dot(cross));
          totalAngle = unsignedAngle * (direction === 0 ? 1 : direction);
        } else {
          totalAngle = unsignedAngle;
        }
      }
    }
    if (Math.abs(totalAngle) < 1e-12) {
      totalAngle = 2 * Math.PI;
    }

    const arcLength = Math.abs(radius * totalAngle);
    if (arcLength < 1e-12) {
      return center.clone().addScaledVector(uUnit, radius);
    }
    const t = clampNumber(length / arcLength, 0, 1);
    const angle = t * totalAngle;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    return center.clone().add(
      uUnit.clone().multiplyScalar(radius * cosA).add(vUnit.clone().multiplyScalar(radius * sinA)),
    );
  }

  const start = cloneVector3(curve.data.startPoint);
  const end = cloneVector3(curve.data.endPoint);
  if (start && end) {
    const delta = end.clone().sub(start);
    const totalLength = delta.length();
    if (Number.isFinite(totalLength) && totalLength > 1e-9) {
      const t = clampNumber(length / totalLength, 0, 1);
      return start.clone().addScaledVector(delta, t);
    }
  }

  const axis = curve.data.axis;
  const origin = cloneVector3(curve.data.origin) ?? new THREE.Vector3(0, 0, 0);
  if (axis === 'x') {
    return origin.clone().add(new THREE.Vector3(length, 0, 0));
  }
  if (axis === 'y') {
    return origin.clone().add(new THREE.Vector3(0, length, 0));
  }
  if (axis === 'z') {
    return origin.clone().add(new THREE.Vector3(0, 0, length));
  }
  return null;
}

/**
 * 将点转换到目标坐标系。
 *
 * @param point 输入点。
 * @param frame 目标坐标系。
 * @returns 转换后的点。
 */
export function transformPointFromCoordinateFrameByFrame(
  point: THREE.Vector3,
  frame: ReferenceCoordinateFrame,
): THREE.Vector3 {
  if (frame === ReferenceCoordinateFrame.Assembly) {
    return point.clone().add(new THREE.Vector3(10, 0, 0));
  }
  if (frame === ReferenceCoordinateFrame.ComponentLocal) {
    return point.clone().add(new THREE.Vector3(0, 20, 0));
  }
  return point.clone();
}
